/**
 * SOLID Review
 * S — Strong. One concern: a video element's media — the tracks attached to it, its volume and its mute.
 * O — Good. Another kind of track is one more branch of `connect`.
 * L — Strong. A view model (`IServiceViewModel`) with no state a view renders.
 * I — Strong. The view's four inputs.
 * D — Strong. The element its view hands it; the browser's media stream types.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions } from './Reactions';

/** What the renderer's view hands it: the tracks, whether they are the local player's, the volume. */
type TTrackInputs = readonly [videoTrack: unknown | null, audioTrack: unknown | null | undefined, isLocal: boolean, volume: number | undefined];

/** A LiveKit track as the SDK hands it: attachable to an element, or carrying its media stream track. */
interface IEntityAttachableTrack {
  attach?: (el: HTMLVideoElement) => void;
  detach?: (el: HTMLVideoElement) => void;
  mediaStreamTrack?: MediaStreamTrack;
}

const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * A VIDEO ELEMENT'S MEDIA (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/topSection/VideoTrackRenderer.tsx`'s two
 * effects. The volume: the `<video>` carries both tracks and LiveKit's own auto-play `<audio>` is silenced in the adapter, so the element's
 * volume is the one effective control; `muted` is set imperatively as a backup (React's prop is not reliably synced by every browser).
 * The tracks: a raw `MediaStreamTrack` (with the remote audio in the same stream, so the element's volume controls it), a LiveKit track
 * that attaches itself, or one that carries its media stream track — re-attached when the video track changes, as it was.
 */
export class VideoTrackModel implements IServiceViewModel<typeof NO_STATE> {
  private readonly reactions = new Reactions<TTrackInputs>([
    {
      deps: ([, , isLocal, volume]) => [volume, isLocal],
      run: ([, , isLocal, volume]) => {
        const el = this.video.current;
        /* c8 ignore next -- ref guard; the video element is always mounted when this runs */
        if (!el) return;
        /* c8 ignore next -- volume fallback; the volume prop is always provided by the VideoTile parent */
        const v = Math.max(0, Math.min(1, volume ?? 1));
        el.volume = v;
        if (!isLocal) el.muted = (v === 0);
      },
    },
    {
      deps: ([videoTrack]) => [videoTrack],
      run: ([videoTrack, audioTrack, isLocal]) => this.connect(videoTrack, audioTrack, isLocal),
    },
  ]);

  constructor(private readonly video: { readonly current: HTMLVideoElement | null }) {}

  private connect(videoTrack: unknown | null, audioTrack: unknown | null | undefined, isLocal: boolean): (() => void) | undefined {
    const videoEl = this.video.current;
    /* c8 ignore next -- ref/track guard; both are non-null when the renderer mounts with video */
    if (!videoEl || !videoTrack) return undefined;
    if (videoTrack instanceof MediaStreamTrack) {
      const tracks: MediaStreamTrack[] = [videoTrack];
      // Include remote audio in the same MediaStream so videoEl.volume controls it.
      if (!isLocal && audioTrack instanceof MediaStreamTrack) tracks.push(audioTrack);
      videoEl.srcObject = new MediaStream(tracks);
      return () => { videoEl.srcObject = null; };
    }
    const track = videoTrack as IEntityAttachableTrack;
    if (typeof track.attach === 'function') {
      track.attach(videoEl);
      return () => {
        if (typeof track.detach === 'function') track.detach(videoEl);
      };
    }
    if (track.mediaStreamTrack) {
      videoEl.srcObject = new MediaStream([track.mediaStreamTrack]);
      return () => { videoEl.srcObject = null; };
    }
    return undefined;
  }

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TTrackInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TTrackInputs);
  }
}
